// The sign-out's second half: ending the browser's session at the identity
// provider arpIQ runs (ADR-0261, Accepted 2026-10-05; #1412).
//
// `POST /auth/logout` ends Zenith's session and clears its cookie first,
// always. For a session minted from an identity it answers `provider_sign_out`,
// and this sends the browser to the provider's `end_session_endpoint` with the
// client, the login name kept from the exchange as `logout_hint`, and this
// host's signed-out page to come back to. The ID token is not here and never
// was: Zenith discarded it at the exchange (ADR-0103 decision 9.1).
//
// **A module of its own, beside `provider.ts` and not inside it.** The session
// imports this, the session is the shell's in every workspace, and Operate's
// write guard (`operate.test.ts`, ADR-0149) walks everything the shell imports:
// `provider.ts` carries the invitation's acceptance, which is not the caller's
// own session and has no business in that walk. The one request here is a read
// of the provider's public discovery document, named on the guard's list.
//
// **The provider is a third party the browser is sent to by design**, as it is
// for the sign-in (`provider.ts`): this calls nothing of Zenith's that `/v1`
// does not expose.
import type { components } from "../api/schema";

export type ProviderSignOut = components["schemas"]["ProviderSignOut"];

/** Where the provider sends the browser after it ended its session there
 * (ADR-0261 decision 4a). Registered on the client per host, beside
 * `/auth/callback`, and like it a path the page recognises rather than a
 * route. */
export const SIGNED_OUT_PATH = "/auth/signed-out";

const KEY = "zenith:signed-out";

/** What the signed-out page is told by the sign-out that sent the browser
 * away: which sentence to show (decision 6a), whether the provider could not be
 * reached (decision 5a), and an invitation to return to (decision 4a). */
export interface SignedOut {
  federated: boolean;
  unreachable?: boolean;
  return_to?: string;
}

/** Where the browser is sent. An object so a test can replace the one act
 * jsdom cannot perform — leaving the page — without touching `location`. */
export const leave = { assign: (url: string) => window.location.assign(url) };

function remember(out: SignedOut) {
  try {
    sessionStorage.setItem(KEY, JSON.stringify(out));
  } catch {
    // Storage refused: the signed-out page then says the plain sentence and
    // returns nowhere, which is true of every session.
  }
}

/** What the sign-out remembered, removed as it is read: a reload of the
 * signed-out page is a page with nothing to say beyond "signed out". */
export function takeSignedOut(): SignedOut | undefined {
  try {
    const raw = sessionStorage.getItem(KEY);
    sessionStorage.removeItem(KEY);
    return raw ? (JSON.parse(raw) as SignedOut) : undefined;
  } catch {
    return undefined;
  }
}

/** A path on this origin, or nothing: the only place a sign-out returns to is
 * the invitation it began on, and anything that could leave the origin is not
 * that. `provider.ts`'s `safeReturn` holds the same rule for the sign-in; it is
 * restated here so the shell's session does not import the acceptance. */
function onThisOrigin(to: string | undefined): string | undefined {
  if (!to || !to.startsWith("/") || to.startsWith("//") || to.startsWith("/\\")) return undefined;
  return to;
}

/** The provider's `end_session_endpoint`, or undefined when its discovery
 * document cannot be read or is not its own. */
async function endSessionEndpoint(issuer: string): Promise<string | undefined> {
  try {
    const r = await globalThis.fetch(issuer.replace(/\/+$/, "") + "/.well-known/openid-configuration");
    if (!r.ok) return undefined;
    const d = (await r.json()) as { issuer?: unknown; end_session_endpoint?: unknown };
    // A document naming another issuer is not this provider's.
    if (d.issuer !== issuer || typeof d.end_session_endpoint !== "string" || d.end_session_endpoint === "") return undefined;
    return d.end_session_endpoint;
  } catch {
    return undefined;
  }
}

/** Where the browser goes to finish a sign-out whose Zenith half is already
 * done (ADR-0261 decisions 2a, 3a, 4a and 5a).
 *
 * With `logout_hint`, ZITADEL ends its session without asking. Without one it
 * shows its own account chooser and ends nothing until the person presses
 * their account. A `post_logout_redirect_uri` the client does not list is
 * refused with a `400`. All three were measured on 2026-10-05
 * (`docs/evidence/2026-10-05-zitadels-end-session-…`).
 *
 * **When the discovery read fails, the browser stays**: the answer is this
 * host's own signed-out page, told the provider could not be reached, rather
 * than a browser error on the provider's hostname. Zenith's session is ended
 * either way. `returnTo` is an invitation the sign-out began on, which the
 * signed-out page returns to. */
export async function signOutAddress(p: ProviderSignOut, returnTo?: string): Promise<string> {
  const back = onThisOrigin(returnTo);
  const endpoint = await endSessionEndpoint(p.issuer);
  if (!endpoint) {
    remember({ federated: p.federated, unreachable: true, ...(back ? { return_to: back } : {}) });
    return SIGNED_OUT_PATH;
  }
  remember({ federated: p.federated, ...(back ? { return_to: back } : {}) });
  const q = new URLSearchParams({ client_id: p.client_id, post_logout_redirect_uri: window.location.origin + SIGNED_OUT_PATH });
  if (p.logout_hint) q.set("logout_hint", p.logout_hint);
  return endpoint + "?" + q.toString();
}
